import { useSuspenseQuery } from '@apollo/client';
import { useMemo, Suspense } from 'react';

import {
  ModuleHeader,
  UpsellBanner,
  WarningMessageBox,
} from '@xing-com/crate-entity-pages-common';
import { useEditContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { useModulesContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-modules-context';
import {
  JobsModuleSimilarJobsDocument,
  createJobsCards,
  JobsCardsCarousel,
  JobsEmptyState,
  JobsSkeleton,
} from '@xing-com/crate-entity-pages-jobs';

import * as Styled from './section-similar-jobs.styles';

const SectionSimilarJobsInner: React.FC = () => {
  const { industryId } = useModulesContext();
  const { isEditing } = useEditContext();

  const { data, error, refetch } = useSuspenseQuery(
    JobsModuleSimilarJobsDocument,
    {
      variables: {
        similarJobsLimit: 20,
        returnAggregations: false,
        similarJobsQuery: {
          filter: industryId ? { industry: { id: [industryId] } } : null,
        },
      },
      errorPolicy: 'ignore',
      notifyOnNetworkStatusChange: true,
    }
  );

  const jobsCards = useMemo(() => {
    const similarJobs = data?.jobSearchByQuery?.collection ?? [];
    // @ts-expect-error query generated as null
    return createJobsCards(similarJobs);
  }, [data]);

  return (
    <>
      {isEditing ? (
        <Styled.BannerEditModeWrapper>
          <UpsellBanner
            headerCopyKey="EP_UPSELL_COMPANY_RECOMMENDED_HEADER"
            bodyCopyKey="EP_UPSELL_COMPANY_RECOMMENDED_BODY"
            moduleName="jobsrecommendationsmodule"
            isProductPage
          />
        </Styled.BannerEditModeWrapper>
      ) : null}
      <div data-testid="JOBS_MODULE">
        <JobsCardsCarousel
          cards={jobsCards}
          onCardBookmarkUpdate={() => refetch()}
        />
      </div>
      {error ? (
        <div data-testid="errorContainer">
          <WarningMessageBox
            headerText="EP_ERROR_HEADER"
            bodyText="EP_ERROR_BODY"
            buttonText="EP_ERROR_RELOAD_CTA"
            onClick={() => refetch()}
          />
        </div>
      ) : null}
      {jobsCards.length === 0 ? (
        <div data-testid="JOBS_CARD">
          <JobsEmptyState />
        </div>
      ) : null}
    </>
  );
};

export const SectionSimilarJobs: React.FC = () => {
  return (
    <div data-testid="SIMILAR-JOBS-CONTAINER">
      <ModuleHeader headlineCopyKey="EP_SIMILAR_JOBS_HEADLINE" />
      <Suspense fallback={<JobsSkeleton />}>
        <SectionSimilarJobsInner />
      </Suspense>
    </div>
  );
};
